import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { BrowserQRCodeReader } from "@zxing/browser";
import "./LiveTicketScannerModal.css";

function defaultParseResult(rawText) {
    return String(rawText || "").trim();
}

const LiveCodeScannerModal = ({ isOpen, toggle, onDetected, parseResult = defaultParseResult, title = "Live QR Scan", guideText = "Align QR code inside frame" }) => {
    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const controlsRef = useRef(null);
    const onDetectedRef = useRef(onDetected);
    const toggleRef = useRef(toggle);
    const parseResultRef = useRef(parseResult);

    const [scanError, setScanError] = useState("");
    const [isStarting, setIsStarting] = useState(false);

    if (!readerRef.current) {
        readerRef.current = new BrowserQRCodeReader();
    }

    function stopScanner() {
        try {
            if (controlsRef.current) {
                controlsRef.current.stop();
                controlsRef.current = null;
            }
            if (readerRef.current) {
                readerRef.current.reset();
            }
        } catch (err) {
            // no-op cleanup
        }
    }

    useEffect(() => {
        onDetectedRef.current = onDetected;
        toggleRef.current = toggle;
        parseResultRef.current = parseResult;
    }, [onDetected, toggle, parseResult]);

    useEffect(() => {
        let isMounted = true;
        let retryTimer = null;

        async function startScanner(attempt = 0) {
            if (!isOpen) return;

            if (!videoRef.current) {
                if (attempt < 20) {
                    retryTimer = setTimeout(() => startScanner(attempt + 1), 50);
                } else if (isMounted) {
                    setScanError("Camera did not initialize. Close and try again.");
                }
                return;
            }

            setIsStarting(true);
            setScanError("");

            try {
                controlsRef.current = await readerRef.current.decodeFromConstraints({ video: { facingMode: { ideal: "environment" } } }, videoRef.current, (result) => {
                    if (!isMounted || !result) return;

                    const parsed = parseResultRef.current(result.getText());
                    if (!parsed) return;

                    stopScanner();
                    if (onDetectedRef.current) onDetectedRef.current(parsed);
                    if (toggleRef.current) toggleRef.current();
                });
            } catch (err) {
                if (isMounted) {
                    setScanError("Camera unavailable or permission denied. Try manual input.");
                }
            } finally {
                if (isMounted) setIsStarting(false);
            }
        }

        startScanner();

        return () => {
            isMounted = false;
            if (retryTimer) clearTimeout(retryTimer);
            stopScanner();
        };
    }, [isOpen]);

    function handleClose() {
        stopScanner();
        toggle();
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            fade={false}
            className="live-scan-modal">
            <ModalHeader toggle={handleClose}>{title}</ModalHeader>
            <ModalBody>
                <div className="live-scan-video-wrap">
                    <video
                        ref={videoRef}
                        className="live-scan-video"
                        muted
                        playsInline
                        autoPlay
                    />
                    <div className="live-scan-guide">{guideText}</div>
                </div>

                {isStarting ? <div className="live-scan-msg">Starting camera...</div> : null}
                {scanError ? <div className="live-scan-error">{scanError}</div> : null}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={handleClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LiveCodeScannerModal;
